import {shortcutsPath} from '@github-ui/paths'
import {useMutation} from '@github-ui/react-query'
import {verifiedFetchJSON} from '@github-ui/verified-fetch'
import {useConfirm} from '@primer/react'
import {useState} from 'react'

import type {SidebarLink, UserShortcut} from '../types'
import {isValidShortcut} from '../utils/shortcut'
import {extractResponseErrorMessage} from '../utils/shortcut-error'

export type EditableShortcutField = 'name' | 'description' | 'query' | 'icon' | 'color'

const defaultErrorMessage = 'Failed to save shortcut'

interface Params {
  type: UserShortcut['searchType']
  onShortcutSaved: (savedShortcut: SidebarLink) => void
}

export function useShortcutEditor({type, onShortcutSaved}: Params) {
  const confirm = useConfirm()
  const [shortcut, setShortcut] = useState<UserShortcut | undefined>(undefined)
  const [isDirty, setIsDirty] = useState(false)

  const saveAndDismissMutation = useMutation({
    mutationFn: async (shortcutToSave: UserShortcut) => {
      const response = await verifiedFetchJSON(shortcutsPath(shortcutToSave.id), {
        method: shortcutToSave.id ? 'PUT' : 'POST',
        body: {view: shortcutToSave},
      })

      if (!response.ok) {
        throw new Error(await extractResponseErrorMessage(response, defaultErrorMessage))
      }

      const savedShortcut = (await response.json()).shortcut as UserShortcut
      return savedShortcut
    },
    onSuccess: savedShortcut => {
      onShortcutSaved({...savedShortcut, source: 'user'})
      doDismiss()
    },
  })

  function create(query: string, baseShortcut?: SidebarLink) {
    const newShortcut: UserShortcut = {
      id: '',
      name: '',
      searchType: type,
      query,
      description: '',
      icon: 'bookmark',
      color: 'gray',
    }

    if (baseShortcut) {
      newShortcut.icon = baseShortcut.icon
      newShortcut.color = baseShortcut.color
    }

    setShortcut(newShortcut)
    setIsDirty(false)
    saveAndDismissMutation.reset()
  }

  function edit(shortcutToEdit: SidebarLink, query: string) {
    if (shortcut) {
      throw new Error('A shortcut is already being edited. Cannot edit another one simultaneously.')
    }
    setShortcut({...shortcutToEdit, query})
    setIsDirty(false)
  }

  async function dismiss(): Promise<boolean> {
    const shouldDiscard =
      !isDirty ||
      (await confirm({
        title: 'Discard changes',
        content: `Are you sure you want to discard changes?`,
        confirmButtonContent: 'Discard',
        confirmButtonType: 'danger',
      }))

    if (!shouldDiscard) return false

    doDismiss()
    return true
  }

  function doDismiss() {
    setShortcut(undefined)
    saveAndDismissMutation.reset()
  }

  function setValue(fieldName: EditableShortcutField, value: string) {
    setShortcut(prevShortcut => {
      if (!prevShortcut) {
        throw new Error('No shortcut is being edited currently. Updating should not be possible.')
      }
      return {...prevShortcut, [fieldName]: value}
    })
    setIsDirty(true)
  }

  async function saveAndDismiss() {
    if (!shortcut) {
      throw new Error('No shortcut is being edited currently. Saving should not be possible.')
    }

    if (saveAndDismissMutation.isPending) return
    if (!isValidShortcut(shortcut)) return

    saveAndDismissMutation.mutate(shortcut)
  }

  return {
    shortcut,
    setValue,
    create,
    edit,
    dismiss,
    saveAndDismiss,
    isSaving: saveAndDismissMutation.isPending,
    errorMessage: saveAndDismissMutation.error?.message,
  }
}
